import React, { useEffect, useState } from "react";
import _ from "lodash";
import { DateTime } from "luxon";
import {
  AppState,
  CustomDataStorage,
  CustomReport,
  Device,
  Division,
  Equipment,
  EquipmentModel,
  EquipmentTime,
  EquipmentType,
  FShift,
  Person,
  Producer,
  ScreenType,
  Shift,
  StaffPost,
  TriggerType,
} from "../types/types";
import { useAppStore } from "../stores/RootStore";
import { useFetch } from "../hooks/useFetch";
import axios from "axios";
import { getOptions } from "../helpers/getOptions";
import { sliceArray } from "../helpers/sliceArray";
import { useGetUrl } from "../hooks/useGetUrl";
import { useEquipmentModels } from "../hooks/useFetchStateHooks/useEquipmentModels";
import { useFetchDevices } from "../hooks/useFetchStateHooks/useFetchDevices";
import { useEquipmentTypes } from "../hooks/useFetchStateHooks/useEquipmentTypes";
import { useStaffPosts } from "../hooks/useFetchStateHooks/useStaffPosts";
import { useNavigate } from "react-router-dom";
import { LoadingLine } from "../ui/LoadingLine/LoadingLine";
import { publicConfig } from "@/config/publicConfig";

let RootContext = React.createContext<AppState>(null!);

export function RootProvider({ children }: { children: React.ReactNode }) {
  let store = useAppStore();
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true);
  const [{ response: appsData }, doFetchApplications] = useFetch(`/users/account`);
  const [{ response: shiftsData, isLoading: isLoadingShifts }, doFetchShifts] = useFetch(`/directories/working_shifts`);
  const [{ response: divisionsData, isLoading: isLoadingDivisions}, doFetchDivisions] = useFetch(`/directories/divisions`);
  const [{ response: personsData, isLoading: isLoadingPersons }, doFetchPersons] = useFetch(`/directories/personnels`);
  const [{ response: equipmentsData, isLoading: isLoadingEquipments }, doFetchEquipment] = useFetch(`/directories/equipment_items`);
  const [{ response: producersData, isLoading: isLoadingProducers }, doFetchProducers] = useFetch("/directories/producers");
  const [{ response: screensData, isLoading: isLoadingScreens }, doFetchScreens] = useFetch(`/directories/screens`);
  const [{ response: customStorageData, isLoading: isLoadingDataStorage }, doFetchCustomStorage] = useFetch(`/directories/custom_data_storages`);
  const [{ response: equipmentModelsData, isLoading: isLoadingModels }, doFetchEquipmentModels] = useFetch("/directories/equipment_models");
  const [{ response: devicesData, isLoading: isLoadingDevices }, doFetchDevices] = useFetch("/devices/devices");
  const [{ response: equipmentTypesData, isLoading: isLoadingTypes }, doFetchEquipmentTypes] = useFetch("/directories/equipment_types");
  const [{ response: staffPostsData, isLoading: isLoadingPosts }, doFetchStaffPosts] = useFetch("/directories/posts");
  const [{ response: triggersData, isLoading: isLoadingTriggers }, doFetchTriggers] = useFetch("/directories/triggers");

  useEffect(() => {
    if (!store.currentUser) return;
    // При отсутствии лицензии перенаправление на страницу для ввода ключа организации
    if (publicConfig.licensingEnabled && !store.licenseChecked) {
      navigate('/license');
      return
    }
    setLoading(true)
    // doFetchApplications()
    doFetchShifts();
    doFetchDivisions();
    doFetchPersons();
    doFetchEquipment();
    doFetchProducers();
    
    doFetchEquipmentModels();
    doFetchDevices();
    doFetchEquipmentTypes();
    doFetchStaffPosts();
    doFetchScreens();
    doFetchCustomStorage();
    doFetchTriggers();
  }, []);

  useEffect(() => {
    if (
      !shiftsData && 
      !divisionsData && 
      !equipmentsData && 
      !producersData && 
      !personsData && 
      !screensData && 
      !customStorageData &&
      !equipmentModelsData &&
      !devicesData &&
      !equipmentTypesData &&
      !staffPostsData &&
      !triggersData
    ) {
      return;
    }

    if (
      isLoadingShifts || 
      isLoadingDivisions || 
      isLoadingPersons ||
      isLoadingEquipments || 
      isLoadingProducers ||
      isLoadingScreens ||
      isLoadingDataStorage ||
      isLoadingModels ||
      isLoadingDevices ||
      isLoadingTypes ||
      isLoadingPosts ||
      isLoadingTriggers
    ) {
      return;
    }

    let base_shift = {
      id: "full",
      name: "Полный день",
      start_at: DateTime.local().startOf("day"),
      end_at: DateTime.local().endOf("day"),
      working_shift_breaks: [],
    };

    if (shiftsData) {
      let shifts = (shiftsData?.data as unknown as Shift[]).map((s) => {
        // Пересборка того что приходит с бэка
        let start = DateTime.fromISO(s.attributes.started_at)
        let end = DateTime.fromISO(s.attributes.ended_at)
        let build_start = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: start.hour, minute: start.minute, second: start.second})
        let build_end = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: end.hour, minute: end.minute, second: end.second})


        return {id: s.id,
          name: s.attributes.name,
          start_at: build_start,
          end_at: build_end,
          working_shift_breaks: s.attributes.working_shift_breaks,
        }
      });


      let site_shifts = [base_shift, ...shifts] as FShift[];
      store.setShifts(site_shifts);
      store.setCurrentShift(base_shift);
    }
    
    if (divisionsData) {
      let divisions = divisionsData?.data as unknown as Division[];
      store.setDivisions(divisions);
      store.setCurrentDivision(
        _.orderBy(
          divisions?.filter((d: Division) => d.attributes.depth === 0),
          "id",
          "asc"
        )[0]
      );
    }

    if (equipmentsData) {
      let equipments = equipmentsData?.data as unknown as Equipment[];
      store.setEquipments(equipments);
    }

    if (producersData) {
      let producers = producersData?.data as unknown as Producer[];
      store.setProducers(producers);
    }

    if (personsData) {
      let persons = personsData?.data as unknown as Person[];
      store.setPersons(persons);
    }

    if (screensData) {
      if (store.currentSite?.user_status == "root" || store.currentUser?.attributes.admin)
        store.setScreens(screensData.data as unknown as ScreenType[])
      else {
        let rawScreens = screensData.data as unknown as ScreenType[];
        let filteredScreens = rawScreens.filter((screen) =>
          screen.attributes.public
            ? true
            : screen.attributes.user_relations.find((user) => {
                return user.user_relation.email == store.currentUser?.attributes?.email;
              })
        );
        store.setScreens(filteredScreens);
      }
    }

    if (customStorageData) {
      let customReports = customStorageData.data as unknown as CustomDataStorage[]
      store.setReports(customReports)
    }

    if (equipmentModelsData) {
      let equipmentModels = equipmentModelsData?.data as unknown as EquipmentModel[];
      if (equipmentModels) store.setEquipmentModels(equipmentModels);
    }

    if (devicesData) {
      let devices = devicesData?.data as unknown as Device[];
      if (devices) store.setDevices(devices);
    }

    if (equipmentTypesData) {
      let equipmentTypes = equipmentTypesData?.data as unknown as EquipmentType[];
      if (equipmentTypes) store.setEquipmentTypes(equipmentTypes);
    }

    if (staffPostsData) {
      let staffPosts = staffPostsData?.data as unknown as StaffPost[];
      if (staffPosts) store.setPosts(staffPosts);
    }

    if (triggersData) {
      let triggers = triggersData?.data as unknown as TriggerType[];
      if (triggers) store.setTriggers(triggers);
    }
    setLoading(false)
  }, [
    shiftsData, 
    divisionsData, 
    equipmentsData, 
    producersData, 
    personsData, 
    screensData, 
    customStorageData,
    devicesData,
    equipmentModelsData,
    equipmentTypesData,
    staffPostsData,
    triggersData,
    isLoadingShifts,
    isLoadingDivisions, 
    isLoadingPersons, 
    isLoadingEquipments,
    isLoadingProducers, 
    isLoadingScreens, 
    isLoadingDataStorage,
    isLoadingModels,
    isLoadingDevices,
    isLoadingTypes,
    isLoadingPosts,
    isLoadingTriggers
  ]);

  // const endpoint = useGetUrl("/directories/equipment_items/equipments_daily_data");
  // useEffect(() => {
  //   // Тест запроса данных работы/простоя по оборудованию
  //   let equipments = equipmentsData?.data as unknown as Equipment[];
  //   let equipmentsIds = equipments?.map((equipment) => equipment.id);

  //   if (equipmentsIds != undefined) {
  //     const parts = sliceArray(equipmentsIds, 3);

  //     const delay = (ms: number) => {
  //       return new Promise((resolve) => {
  //         setTimeout(resolve, ms);
  //       });
  //     };

  //     Promise.all(
  //       parts.map(async (part, ind) => {
  //         await delay(500 * ind);
  //         return await axios.get(endpoint, getOptions(part));
  //       })
  //     ).then((allData) => {
  //       const equipmentsTime = allData.reduce((prev: EquipmentTime[], obj) => {
  //         return [...prev, ...obj.data.data];
  //       }, []);
  //       store.setEquipmentsTime(equipmentsTime);
  //     });
  //   }
  // }, []);

  let value = { ...store };
  return (
    <RootContext.Provider value={value}>{loading ? <LoadingLine /> : children }</RootContext.Provider>
  );
}

export function useRoot() {
  return React.useContext(RootContext);
}
